import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Typography, Grid, Card, CardHeader, CardContent, Button,
  FormControl, InputLabel, Select, MenuItem, Checkbox, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Stack, Divider, Snackbar, Alert, CircularProgress,
  Chip, IconButton, Tooltip, Dialog, DialogContent, DialogTitle,
  List, ListItemButton, ListItemText
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import { fetchJson } from '../../../utils/fetchJson';
import useAyPhaseReady from '../../../utils/useAyPhaseReady';
import useScopeAutoRefresh from '../../../hooks/useScopeAutoRefresh';
import { useTranslation } from 'react-i18next';

const formatStudentLabel = (student, t) => {
  if (!student) return t ? t('schoolAdmin.reportCardStudio.studio.labels.student') : 'Student';
  const name = [student.firstName, student.lastName].filter(Boolean).join(' ').trim();
  if (name) return name;
  return student.studentNumber || student.matricule || (t ? t('schoolAdmin.reportCardStudio.studio.labels.student') : 'Student');
};

const describeClass = (cls, t) => {
  if (!cls) return t ? t('schoolAdmin.reportCardStudio.studio.labels.class') : 'Class';
  return cls.name || cls.className || (t ? t('schoolAdmin.reportCardStudio.studio.labels.unnamedClass') : 'Unnamed Class');
};

const formatReportLabel = (reportType, sequenceNumber, termNumber, t) => {
  if (reportType === 'sequence') {
    return t ? t('schoolAdmin.reportCardStudio.studio.reportLabels.sequence', { number: sequenceNumber }) : `Sequence ${sequenceNumber}`;
  }
  if (reportType === 'term') {
    if (termNumber === 1) return t ? t('schoolAdmin.reportCardStudio.enhanced.termMenu.1') : 'First Term';
    if (termNumber === 2) return t ? t('schoolAdmin.reportCardStudio.enhanced.termMenu.2') : 'Second Term';
    if (termNumber === 3) return t ? t('schoolAdmin.reportCardStudio.enhanced.termMenu.3') : 'Third Term';
    return t ? t('schoolAdmin.reportCardStudio.studio.reportLabels.term', { number: termNumber }) : `Term ${termNumber}`;
  }
  return t ? t('schoolAdmin.reportCardStudio.studio.reportLabels.annual') : 'Annual Report';
};

const safeRevokeObjectUrl = (url) => {
  if (!url || typeof window === 'undefined') return;
  try {
    window.URL.revokeObjectURL(url);
  } catch (err) {
    console.warn('Failed to revoke object URL', err);
  }
};

const resolveApiBase = () => {
  const configuredBaseRaw = (
    process.env.REACT_APP_API_BASE_URL ||
    process.env.VITE_API_BASE_URL ||
    process.env.NEXT_PUBLIC_API_BASE_URL ||
    ''
  ).trim();
  const browserHost = typeof window !== 'undefined' ? window.location.hostname : '';
  const isLocalBrowserHost = ['localhost', '127.0.0.1', '0.0.0.0'].includes(browserHost);
  const isLocalNodeEnv = process.env.NODE_ENV === 'development';
  const baseRaw = configuredBaseRaw || ((isLocalBrowserHost || isLocalNodeEnv) ? 'http://localhost:5000' : '');
  return (baseRaw || '').replace(/\/$/, '');
};

const resolveAccessToken = () => {
  const tokenKeys = ['impersonationToken', 'accessToken', 'token', 'authToken', 'jwt', 'id_token', 'access_token', 'ACCESS_TOKEN'];
  for (const k of tokenKeys) {
    const v = (typeof window !== 'undefined') ? (localStorage.getItem(k) || sessionStorage.getItem(k)) : null;
    if (v) return v;
  }
  try {
    const raw = (typeof window !== 'undefined') ? (localStorage.getItem('user') || sessionStorage.getItem('user')) : null;
    if (raw) {
      const obj = JSON.parse(raw);
      const candidate = obj?.accessToken || obj?.token || obj?.authToken || obj?.jwt || obj?.id_token;
      if (candidate) return candidate;
    }
  } catch (_) {}
  return '';
};

const TEMPLATE_OPTIONS = [
  {
    id: 'advanced',
    labelKey: 'schoolAdmin.reportCardStudio.studio.templates.advanced.label',
    descriptionKey: 'schoolAdmin.reportCardStudio.studio.templates.advanced.description'
  }
];

const ReportCardStudioPage = () => {
  const { t } = useTranslation();
  const { ready: scopeReady, activePhase } = useAyPhaseReady();
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfTabs, setPdfTabs] = useState([]);
  const [activePdfTab, setActivePdfTab] = useState(0);
  const pdfTabsRef = useRef([]);
  useEffect(() => {
    pdfTabsRef.current = pdfTabs;
  }, [pdfTabs]);
  useEffect(() => () => {
    pdfTabsRef.current.forEach((tab) => {
      if (tab?.url) {
        window.URL.revokeObjectURL(tab.url);
      }
    });
  }, []);

  const [selectedClass, setSelectedClass] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('');
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [reportType, setReportType] = useState('term'); // 'sequence', 'term', or 'annual'
  const [sequenceNumber, setSequenceNumber] = useState(1); // 1-6 for sequences
  const [termNumber, setTermNumber] = useState(1); // 1, 2, or 3
  const [academicYear, setAcademicYear] = useState('2024/2025');
  const [academicYears, setAcademicYears] = useState([]);
  const [yearsLoading, setYearsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [available, setAvailable] = useState({ sequences: [], terms: [], annualAvailable: false });
  const [templateEngine, setTemplateEngine] = useState('advanced'); // 'advanced' (PDF template) or 'enhanced' (programmatic)
  const [reportLanguage, setReportLanguage] = useState('en'); // 'en' | 'fr' — translates the card body (not the letterhead)
  const [letterheadTemplate, setLetterheadTemplate] = useState('default'); // letterhead format for official documents

  const phaseCode = useMemo(() => {
    const raw = (activePhase || localStorage.getItem('schoolAdmin_activePhase') || '').toString().trim();
    return raw || 'nur_pri';
  }, [activePhase]);

  const levelOptions = useMemo(() => {
    const code = (phaseCode || '').toString().trim().toLowerCase();
    if (code === 'sec') {
      return [
        { value: 'first_cycle', label: t('schoolAdmin.reportCardStudio.studio.levelOptions.firstCycle') },
        { value: 'second_cycle', label: t('schoolAdmin.reportCardStudio.studio.levelOptions.secondCycle') }
      ];
    }
    return [
      { value: 'nursery', label: t('schoolAdmin.reportCardStudio.studio.levelOptions.nursery') },
      { value: 'primary', label: t('schoolAdmin.reportCardStudio.studio.levelOptions.primary') }
    ];
  }, [phaseCode, t]);

  // Default the language + letterhead selectors to the school's saved settings,
  // so report cards reflect the language/letterhead chosen in School Settings
  // (still overridable per generation here).
  useEffect(() => {
    (async () => {
      try {
        const resp = await fetchJson('/school-setup-and-configuration/school-settings', { method: 'GET' });
        const school = (resp && (resp.data?.school || resp.school || resp.data || resp)) || {};
        const lang = String(school.reportCardLanguage || school.customFields?.reportCardLanguage || 'en').toLowerCase() === 'fr' ? 'fr' : 'en';
        setReportLanguage(lang);
        const lh = school.reportCardLetterhead || school.customFields?.reportCardLetterhead;
        if (['default', 'ministry_school_en', 'ministry_school_fr'].includes(lh)) setLetterheadTemplate(lh);
      } catch (_) {}
    })();
  }, []);

  useEffect(() => {
    if (!selectedLevel && levelOptions.length > 0) {
      setSelectedLevel(levelOptions[0].value);
    }
    if (selectedLevel && levelOptions.length > 0 && !levelOptions.some((o) => o.value === selectedLevel)) {
      setSelectedLevel(levelOptions[0].value);
    }
  }, [selectedLevel, levelOptions]);

  const filteredClasses = useMemo(() => {
    const lvl = (selectedLevel || '').toString().trim().toLowerCase();
    if (!lvl) return classes;
    return (classes || []).filter((c) => (c?.level || '').toString().trim().toLowerCase() === lvl);
  }, [classes, selectedLevel]);

  useEffect(() => {
    if (!selectedClass) return;
    if (filteredClasses.some((c) => String(c.id) === String(selectedClass))) return;
    setSelectedClass('');
    setStudents([]);
    setSelectedStudents([]);
    setAvailable({ sequences: [], terms: [], annualAvailable: false });
  }, [filteredClasses, selectedClass]);

  const templatePreview = useMemo(() => {
    const code = (phaseCode || '').toString().trim().toLowerCase();
    const lvl = (selectedLevel || '').toString().trim().toLowerCase();
    const isNurseryTemplate = code === 'nur_pri' && lvl === 'nursery';
    const isPrimaryTemplate = code === 'nur_pri' && lvl === 'primary';
    const isSecondaryTemplate = code === 'sec';
    if (isNurseryTemplate) {
      return {
        label: t('schoolAdmin.reportCardStudio.studio.templatePreview.nursery.label'),
        description: t('schoolAdmin.reportCardStudio.studio.templatePreview.nursery.description'),
        images: ['/ReportTemplate/nurseryreportthumbnail.png']
      };
    }
    if (isPrimaryTemplate) {
      return {
        label: t('schoolAdmin.reportCardStudio.studio.templatePreview.official.label'),
        description: t('schoolAdmin.reportCardStudio.studio.templatePreview.official.description'),
        images: ['/ReportTemplate/primreportthumbnail.png']
      };
    }
    if (isSecondaryTemplate) {
      return {
        label: t('schoolAdmin.reportCardStudio.studio.templatePreview.official.label'),
        description: t('schoolAdmin.reportCardStudio.studio.templatePreview.official.description'),
        images: ['/ReportTemplate/secreortthumbnail.png']
      };
    }
    return {
      label: t('schoolAdmin.reportCardStudio.studio.templatePreview.official.label'),
      description: t('schoolAdmin.reportCardStudio.studio.templatePreview.official.description'),
      images: ['/ReportTemplate/primreportthumbnail.png']
    };
  }, [phaseCode, selectedLevel, t]);

  const showSnackbar = React.useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  // Try to refresh access token using refresh-token endpoint (cookie-based)
  const refreshAccessToken = useCallback(async () => {
    try {
      const configuredBaseRaw = (
        process.env.REACT_APP_API_BASE_URL ||
        process.env.VITE_API_BASE_URL ||
        process.env.NEXT_PUBLIC_API_BASE_URL ||
        ''
      ).trim();
      const browserHost = typeof window !== 'undefined' ? window.location.hostname : '';
      const isLocalBrowserHost = ['localhost', '127.0.0.1', '0.0.0.0'].includes(browserHost);
      const isLocalNodeEnv = process.env.NODE_ENV === 'development';
      const baseRaw = configuredBaseRaw || ((isLocalBrowserHost || isLocalNodeEnv) ? 'http://localhost:5000' : '');
      const apiBase = (baseRaw || '').replace(/\/$/, '');
      if (!apiBase) return null;

      const res = await fetch(`${apiBase}/api/auth/refresh-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({})
      });
      if (res.ok) {
        const data = await res.json();
        if (data && data.accessToken) {
          localStorage.setItem('token', data.accessToken);
          return data.accessToken;
        }
      }
    } catch (_) {}
    return null;
  }, []);

  // Load classes
  const loadClasses = useCallback(async () => {
    try {
      if (!scopeReady) return;
      const phase = (phaseCode || '').toString().trim() || 'nur_pri';
      const response = await fetchJson(`/api/classes-lite?phase=${encodeURIComponent(phase)}`, { method: 'GET' });
      const data = Array.isArray(response?.classes)
        ? response.classes
        : (response?.data || response || []);
      setClasses(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load classes:', error);
      showSnackbar(t('schoolAdmin.reportCardStudio.enhanced.errors.loadClasses'), 'error');
    }
  }, [showSnackbar, scopeReady, phaseCode, t]);

  const scopeLoaders = useMemo(() => [loadClasses], [loadClasses]);
  useScopeAutoRefresh(scopeLoaders);

  useEffect(() => {
    const onPhaseChanged = async () => {
      setSelectedClass('');
      setSelectedLevel('');
      setStudents([]);
      setSelectedStudents([]);
      setAvailable({ sequences: [], terms: [], annualAvailable: false });
      // Read fresh phase from localStorage to avoid stale closure
      const freshPhase = (localStorage.getItem('schoolAdmin_activePhase') || '').toString().trim() || 'nur_pri';
      try {
        const response = await fetchJson(`/api/classes-lite?phase=${encodeURIComponent(freshPhase)}`, { method: 'GET' });
        const data = Array.isArray(response?.classes)
          ? response.classes
          : (response?.data || response || []);
        setClasses(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Failed to load classes on phase switch:', error);
      }
    };

    window.addEventListener('app:phase:changed', onPhaseChanged);
    return () => window.removeEventListener('app:phase:changed', onPhaseChanged);
  }, []);

  // Get current academic year from school settings or default
  const getCurrentAcademicYear = () => {
    const currentYear = new Date().getFullYear();
    const nextYear = currentYear + 1;
    return `${currentYear}/${nextYear}`;
  };

  // Load academic years from backend (evaluations table)
  const loadAcademicYears = useCallback(async () => {
    setYearsLoading(true);
    try {
      const res = await fetchJson('/api/report-cards/academic-years', { method: 'GET' });
      const years = Array.isArray(res?.years) ? res.years : [];
      setAcademicYears(years);
      if (years.length > 0) {
        // Pick the latest (already sorted DESC server-side)
        setAcademicYear(years[0]);
      } else {
        setAcademicYear(getCurrentAcademicYear());
      }
    } catch (e) {
      console.error('Failed to load academic years:', e);
      // Fallback to current year if backend not ready
      setAcademicYears([]);
      setAcademicYear(getCurrentAcademicYear());
    } finally {
      setYearsLoading(false);
    }
  }, []);

  // Load students for selected class
  const loadStudents = useCallback(async () => {
    if (!selectedClass || !academicYear) {
      setStudents([]);
      setSelectedStudents([]);
      return;
    }

    setLoading(true);
    try {
      const response = await fetchJson(`/api/report-cards/students?classId=${encodeURIComponent(selectedClass)}&academicYear=${encodeURIComponent(academicYear)}`, {
        method: 'GET'
      });
      setStudents(response?.students || []);
      setSelectedStudents([]);
    } catch (error) {
      console.error('Failed to load students:', error);
      showSnackbar(t('schoolAdmin.reportCardStudio.enhanced.errors.loadStudents'), 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedClass, academicYear, showSnackbar, t]);

  useEffect(() => {
    if (!scopeReady) return;
    loadClasses();
    loadAcademicYears();
  }, [scopeReady, loadClasses, loadAcademicYears]);

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  // Load available evaluations with recorded marks for selected class/year
  useEffect(() => {
    (async () => {
      if (!selectedClass || !academicYear) {
        setAvailable({ sequences: [], terms: [], annualAvailable: false });
        return;
      }
      try {
        const data = await fetchJson(`/api/report-cards/available-for-class?classId=${encodeURIComponent(selectedClass)}&academicYear=${encodeURIComponent(academicYear)}`);
        const sequences = Array.isArray(data?.sequences) ? data.sequences : [];
        const terms = Array.isArray(data?.terms) ? data.terms : [];
        const annualAvailable = !!data?.annualAvailable;
        setAvailable({ sequences, terms, annualAvailable });
        // Auto-correct selection if currently invalid
        if (reportType === 'sequence' && sequences.length && !sequences.includes(Number(sequenceNumber))) {
          setSequenceNumber(sequences[0]);
        }
        if (reportType === 'term' && terms.length && !terms.includes(Number(termNumber))) {
          setTermNumber(terms[0]);
        }
      } catch (_) {
        setAvailable({ sequences: [], terms: [], annualAvailable: false });
      }
    })();
  }, [selectedClass, academicYear, reportType]);

  // Toggle student selection
  const toggleStudentSelection = (studentId) => {
    setSelectedStudents(prev => {
      if (prev.includes(studentId)) {
        return prev.filter(id => id !== studentId);
      } else {
        return [...prev, studentId];
      }
    });
  };

  // Select/Deselect all students
  const toggleSelectAll = () => {
    if (selectedStudents.length === students.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(students.map(s => s.id));
    }
  };

  // Generate batch PDF (single merged file) for one or more students
  const generateBatchPdf = async (ids) => {
    if (!ids || ids.length === 0) {
      showSnackbar(t('schoolAdmin.reportCardStudio.studio.errors.selectStudent'), 'error');
      return;
    }
    setGenerating(true);
    try {
      const apiBase = resolveApiBase();
      if (!apiBase) {
        showSnackbar(t('schoolAdmin.reportCardStudio.studio.errors.sessionExpired'), 'error');
        return;
      }

      const selectedClassMeta = classes.find((c) => String(c.id) === String(selectedClass));
      const classLevel = (selectedClassMeta?.level || selectedLevel || '').toString().trim().toLowerCase();
      const isNursery = classLevel === 'nursery';
      const basePath = isNursery ? '/api/nursery-report-cards' : '/api/report-cards';

      // Validate availability
      if (reportType === 'sequence' && !available.sequences.includes(Number(sequenceNumber))) {
        showSnackbar(t('schoolAdmin.reportCardStudio.studio.errors.noMarksForSequence'), 'error');
        return;
      }
      if (reportType === 'term' && !available.terms.includes(Number(termNumber))) {
        showSnackbar(t('schoolAdmin.reportCardStudio.studio.errors.noMarksForTerm'), 'error');
        return;
      }
      if (reportType === 'annual' && !available.annualAvailable) {
        showSnackbar(t('schoolAdmin.reportCardStudio.studio.errors.annualNotAvailable'), 'error');
        return;
      }

      const body = {
        studentIds: ids,
        reportType,
        sequenceNumber: Number(sequenceNumber),
        termNumber: Number(termNumber),
        academicYear,
        classId: selectedClass || null,
        reportLanguage,
        letterheadTemplate
      };

      const token = resolveAccessToken();
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': token.toLowerCase().startsWith('bearer ') ? token : `Bearer ${token}` } : {})
      };
      const storedPhase = localStorage.getItem('schoolAdmin_activePhase');
      if (storedPhase) headers['X-Active-Phase'] = storedPhase;
      const storedPhaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId');
      if (storedPhaseId) headers['X-Active-Phase-Id'] = String(storedPhaseId);
      const scopeSchoolId = localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || localStorage.getItem('schoolId');
      if (scopeSchoolId) headers['X-Scope-School-Id'] = String(scopeSchoolId);

      const requestUrl = `${apiBase}${basePath}/generate/batch-pdf`;

      let response = await fetch(requestUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        credentials: 'include'
      });

      if (!response.ok && (response.status === 401 || response.status === 403)) {
        const newToken = await refreshAccessToken();
        if (!newToken) {
          showSnackbar(t('schoolAdmin.reportCardStudio.studio.errors.sessionExpired'), 'error');
          return;
        }
        response = await fetch(requestUrl, {
          method: 'POST',
          headers: { ...headers, 'Authorization': `Bearer ${newToken}` },
          body: JSON.stringify(body),
          credentials: 'include'
        });
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to generate batch PDF:', response.status, errorData);
        const err = new Error('GENERATE_FAILED');
        err.statusCode = response.status;
        err.serverMessage = errorData?.message || errorData?.error || errorData?.details || '';
        throw err;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      // Extract filename from Content-Disposition header or build fallback
      let filename = t('schoolAdmin.reportCardStudio.studio.files.defaultPdfName');
      const disposition = response.headers.get('Content-Disposition');
      if (disposition) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match) filename = match[1];
      }

      // Build tab label
      const classLabel = selectedClassMeta ? describeClass(selectedClassMeta, t) : t('schoolAdmin.reportCardStudio.studio.labels.class');
      const evalLabel = formatReportLabel(reportType, sequenceNumber, termNumber, t);
      const tabLabel = ids.length === 1
        ? formatStudentLabel(students.find((s) => s.id === ids[0]), t)
        : `${classLabel} (${t('schoolAdmin.reportCardStudio.studio.labels.studentsCount', { count: ids.length })})`;
      const groupKey = `${String(selectedClass || '')}|${String(reportType || '')}|${String(sequenceNumber || '')}|${String(termNumber || '')}|${String(academicYear || '')}`;

      const newTab = {
        id: `batch-${Date.now()}`,
        url,
        filename,
        label: tabLabel,
        studentMeta: ids.length === 1 ? students.find((s) => s.id === ids[0]) : null,
        dock: {
          classId: selectedClass,
          classLabel,
          evaluationLabel: evalLabel,
          groupKey,
          reportType,
          sequenceNumber,
          termNumber,
          academicYear
        },
        config: { endpoint: `${basePath}/generate/batch-pdf`, body, filename },
        createdAt: new Date().toISOString()
      };

      pdfTabsRef.current = [...pdfTabsRef.current, newTab];
      setPdfTabs(pdfTabsRef.current.slice());
      setActivePdfTab(pdfTabsRef.current.length - 1);
      setPdfPreviewOpen(true);

      showSnackbar(t('schoolAdmin.reportCardStudio.studio.snack.generated', { count: ids.length }));
    } catch (error) {
      console.error('Failed to generate batch PDF:', error);
      const serverMsg = error?.serverMessage || '';
      const statusCode = error?.statusCode || 0;
      let displayMsg;
      if (serverMsg) {
        displayMsg = serverMsg;
      } else if (reportType === 'term') {
        displayMsg = t('schoolAdmin.reportCardStudio.studio.errors.noMarksForTerm');
      } else if (reportType === 'sequence') {
        displayMsg = t('schoolAdmin.reportCardStudio.studio.errors.noMarksForSequence');
      } else if (reportType === 'annual') {
        displayMsg = t('schoolAdmin.reportCardStudio.studio.errors.annualNotAvailable');
      } else {
        displayMsg = t('schoolAdmin.reportCardStudio.studio.errors.generateFailed');
      }
      showSnackbar(displayMsg, 'error');
    } finally {
      setGenerating(false);
    }
  };

  // Generate report card for single student
  const generateSingleReport = async (studentId) => {
    await generateBatchPdf([studentId]);
  };

  // Generate bulk report cards (single merged PDF)
  const generateBulkReports = async () => {
    await generateBatchPdf(selectedStudents);
  };

  const numericSequenceNumber = Number(sequenceNumber);
  const numericTermNumber = Number(termNumber);

  const sequenceSelectOptions = useMemo(() => available.sequences || [], [available.sequences]);
  const termSelectOptions = useMemo(() => available.terms || [], [available.terms]);

  const closePdfTab = (index) => {
    const target = pdfTabsRef.current[index];
    safeRevokeObjectUrl(target?.url);
    const updated = pdfTabsRef.current.filter((_, idx) => idx !== index);
    pdfTabsRef.current = updated;
    setPdfTabs(updated);
    if (updated.length === 0) {
      setPdfPreviewOpen(false);
      setActivePdfTab(0);
    } else {
      setActivePdfTab((prev) => Math.max(0, Math.min(prev, updated.length - 1)));
    }
  };

  const downloadCurrentPdf = () => {
    const tab = pdfTabs[activePdfTab];
    if (!tab) return;
    const link = document.createElement('a');
    link.href = tab.url;
    link.download = tab.filename;
    link.click();
  };

  const printCurrentPdf = () => {
    const tab = pdfTabs[activePdfTab];
    if (!tab) return;
    const iframe = document.getElementById(`report-preview-${tab.id}`);
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    }
  };

  const clearPdfTabs = () => {
    pdfTabsRef.current.forEach((tab) => safeRevokeObjectUrl(tab?.url));
    pdfTabsRef.current = [];
    setPdfTabs([]);
    setActivePdfTab(0);
    setPdfPreviewOpen(false);
  };

  const openPdfInWindow = () => {
    const tab = pdfTabs[activePdfTab];
    if (!tab) return;
    window.open(tab.url, '_blank', 'noopener,noreferrer');
  };

  const currentTab = pdfTabs[activePdfTab];
  const hasTabs = pdfTabs.length > 0;
  const selectedClassMeta = useMemo(
    () => classes.find((c) => String(c.id) === String(selectedClass)),
    [classes, selectedClass]
  );
  const dockClassLabel = selectedClassMeta ? describeClass(selectedClassMeta, t) : t('schoolAdmin.reportCardStudio.studio.labels.class');
  const dockEvaluationLabel = formatReportLabel(reportType, sequenceNumber, termNumber, t);
  const dockGroupKey = `${String(selectedClass || '')}|${String(reportType || '')}|${String(sequenceNumber || '')}|${String(termNumber || '')}|${String(academicYear || '')}`;
  const dockTitle = `${dockClassLabel} • ${dockEvaluationLabel}`;
  const dockTabs = useMemo(() => {
    const items = pdfTabs.map((tab, index) => ({ tab, index }));
    const filtered = items.filter((item) => item?.tab?.dock?.groupKey === dockGroupKey);
    return filtered.length ? filtered : items;
  }, [pdfTabs, dockGroupKey]);

  useEffect(() => {
    if (!pdfPreviewOpen) return;
    if (!dockTabs.length) return;
    const inGroup = dockTabs.some((item) => item.index === activePdfTab);
    if (inGroup) return;
    setActivePdfTab(dockTabs[0].index);
  }, [pdfPreviewOpen, dockTabs, activePdfTab]);

  return (
    <Box sx={{ width: '100%', px: { xs: 2, md: 4 }, py: 3, margin: '0 auto' }}>
      {/* Header */}
      <Box sx={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        borderRadius: 4,
        p: 4,
        mb: 4,
        boxShadow: '0 10px 30px rgba(0,0,0,0.15)'
      }}>
        <Typography variant="h3" fontWeight={800} gutterBottom>
          📄 {t('schoolAdmin.reportCardStudio.studio.header.title')}
        </Typography>
        <Typography variant="h6" sx={{ opacity: 0.95 }}>
          {t('schoolAdmin.reportCardStudio.studio.header.subtitle')}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Left Panel - Selection */}
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 3 }}>
            <CardHeader 
              title={t('schoolAdmin.reportCardStudio.studio.selection.title')}
              subheader={t('schoolAdmin.reportCardStudio.studio.selection.subtitle')}
              action={
                <IconButton onClick={() => { loadClasses(); loadStudents(); }}>
                  <RefreshIcon />
                </IconButton>
              }
            />
            <CardContent>
              <Stack spacing={3}>
                <FormControl fullWidth>
                  <InputLabel>{t('schoolAdmin.reportCardStudio.studio.fields.level')}</InputLabel>
                  <Select
                    value={selectedLevel}
                    onChange={(e) => setSelectedLevel(e.target.value)}
                    label={t('schoolAdmin.reportCardStudio.studio.fields.level')}
                  >
                    {levelOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>{t('schoolAdmin.reportCardStudio.studio.fields.class')}</InputLabel>
                  <Select
                    value={selectedClass}
                    onChange={(e) => setSelectedClass(e.target.value)}
                    label={t('schoolAdmin.reportCardStudio.studio.fields.class')}
                  >
                    <MenuItem value="">
                      <em>{t('schoolAdmin.reportCardStudio.studio.placeholders.selectClass')}</em>
                    </MenuItem>
                    {filteredClasses.map((cls) => (
                      <MenuItem key={cls.id} value={cls.id}>
                        {describeClass(cls, t)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>{t('schoolAdmin.reportCardStudio.studio.fields.reportType')}</InputLabel>
                  <Select
                    value={reportType}
                    onChange={(e) => setReportType(e.target.value)}
                    label={t('schoolAdmin.reportCardStudio.studio.fields.reportType')}
                  >
                    <MenuItem value="sequence">{t('schoolAdmin.reportCardStudio.studio.reportTypes.sequence')}</MenuItem>
                    <MenuItem value="term">{t('schoolAdmin.reportCardStudio.studio.reportTypes.term')}</MenuItem>
                    <MenuItem value="annual">{t('schoolAdmin.reportCardStudio.studio.reportTypes.annual')}</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>{t('schoolAdmin.reportCardStudio.studio.fields.language') || 'Report language'}</InputLabel>
                  <Select
                    value={reportLanguage}
                    onChange={(e) => setReportLanguage(e.target.value)}
                    label={t('schoolAdmin.reportCardStudio.studio.fields.language') || 'Report language'}
                  >
                    <MenuItem value="en">{t('schoolAdmin.reportCardStudio.studio.language.en') || 'English'}</MenuItem>
                    <MenuItem value="fr">{t('schoolAdmin.reportCardStudio.studio.language.fr') || 'French'}</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>{t('schoolAdmin.reportCardStudio.studio.fields.letterhead') || 'Letterhead'}</InputLabel>
                  <Select
                    value={letterheadTemplate}
                    onChange={(e) => setLetterheadTemplate(e.target.value)}
                    label={t('schoolAdmin.reportCardStudio.studio.fields.letterhead') || 'Letterhead'}
                  >
                    <MenuItem value="default">{t('schoolAdmin.reportCardStudio.studio.letterhead.default') || 'Default (bilingual ministry)'}</MenuItem>
                    <MenuItem value="ministry_school_en">{t('schoolAdmin.reportCardStudio.studio.letterhead.ministrySchoolEn') || 'Ministry + School info (English)'}</MenuItem>
                    <MenuItem value="ministry_school_fr">{t('schoolAdmin.reportCardStudio.studio.letterhead.ministrySchoolFr') || 'Ministry + School info (French)'}</MenuItem>
                  </Select>
                </FormControl>

                {reportType === 'sequence' && (
                  <FormControl fullWidth>
                    <InputLabel>{t('schoolAdmin.reportCardStudio.studio.fields.sequenceNumber')}</InputLabel>
                    <Select
                      value={sequenceNumber}
                      onChange={(e) => setSequenceNumber(Number(e.target.value))}
                      label={t('schoolAdmin.reportCardStudio.studio.fields.sequenceNumber')}
                    >
                      {available.sequences.length === 0 ? (
                        <MenuItem value={sequenceNumber} disabled>
                          <em>{t('schoolAdmin.reportCardStudio.studio.messages.noSequencesWithMarks')}</em>
                        </MenuItem>
                      ) : (
                        sequenceSelectOptions.map((n) => (
                          <MenuItem key={n} value={n}>{t(`schoolAdmin.reportCardStudio.enhanced.sequenceMenu.${n}`)}</MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                )}

                {reportType === 'term' && (
                  <FormControl fullWidth>
                    <InputLabel>{t('schoolAdmin.reportCardStudio.studio.fields.termNumber')}</InputLabel>
                    <Select
                      value={termNumber}
                      onChange={(e) => setTermNumber(Number(e.target.value))}
                      label={t('schoolAdmin.reportCardStudio.studio.fields.termNumber')}
                    >
                      {available.terms.length === 0 ? (
                        <MenuItem value={termNumber} disabled>
                          <em>{t('schoolAdmin.reportCardStudio.studio.messages.noTermsAvailable')}</em>
                        </MenuItem>
                      ) : (
                        termSelectOptions.map((n) => (
                          <MenuItem key={n} value={n}>{t(`schoolAdmin.reportCardStudio.enhanced.termMenu.${n}`)}</MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                )}

                <FormControl fullWidth>
                  <InputLabel>{t('schoolAdmin.reportCardStudio.studio.fields.academicYear')}</InputLabel>
                  <Select
                    value={academicYear}
                    onChange={(e) => setAcademicYear(e.target.value)}
                    label={t('schoolAdmin.reportCardStudio.studio.fields.academicYear')}
                  >
                    {academicYears.length === 0 ? (
                      <MenuItem value="">
                        <em>{yearsLoading ? t('schoolAdmin.reportCardStudio.studio.messages.loading') : t('schoolAdmin.reportCardStudio.studio.messages.noAcademicYears')}</em>
                      </MenuItem>
                    ) : (
                      academicYears.map((y) => (
                        <MenuItem key={y} value={y}>{y}</MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>

                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('schoolAdmin.reportCardStudio.studio.fields.template')}
                  </Typography>
                  <Grid container spacing={1}>
                    {TEMPLATE_OPTIONS.map((tpl) => (
                      <Grid item xs={6} key={tpl.id}>
                        <Paper
                          onClick={() => setTemplateEngine(tpl.id)}
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            cursor: 'pointer',
                            border: templateEngine === tpl.id
                              ? '2px solid #1976d2'
                              : '1px solid rgba(0,0,0,0.12)',
                            boxShadow: templateEngine === tpl.id
                              ? '0 0 0 1px rgba(25,118,210,0.2)'
                              : 'none',
                            backgroundColor: templateEngine === tpl.id
                              ? 'rgba(25,118,210,0.04)'
                              : 'background.paper'
                          }}
                        >
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {templatePreview.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {templatePreview.description}
                          </Typography>

                          <Grid container spacing={1} sx={{ mt: 1 }}>
                            {(templatePreview.images || []).map((src) => (
                              <Grid item xs={12} key={src}>
                                <Box
                                  component="img"
                                  src={src}
                                  alt={templatePreview.label}
                                  sx={{
                                    width: '100%',
                                    maxHeight: 260,
                                    objectFit: 'contain',
                                    borderRadius: 1,
                                    border: '1px solid rgba(0,0,0,0.12)',
                                    bgcolor: '#fff',
                                    boxShadow: '0 6px 18px rgba(0,0,0,0.08)'
                                  }}
                                />
                              </Grid>
                            ))}
                          </Grid>

                          {/* Thumbnail preview removed as requested */}
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('schoolAdmin.reportCardStudio.studio.fields.selectedCount', { count: selectedStudents.length })}
                  </Typography>
                  <Stack spacing={1} sx={{ mt: 2 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={generating ? <CircularProgress size={20} color="inherit" /> : <PrintIcon />}
                      onClick={generateBulkReports}
                      disabled={!selectedClass || selectedStudents.length === 0 || generating}
                    >
                      {generating ? t('schoolAdmin.reportCardStudio.studio.actions.generating') : t('schoolAdmin.reportCardStudio.studio.actions.generateCount', { count: selectedStudents.length })}
                    </Button>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={toggleSelectAll}
                      disabled={students.length === 0}
                    >
                      {selectedStudents.length === students.length ? t('schoolAdmin.reportCardStudio.enhanced.actions.deselectAll') : t('schoolAdmin.reportCardStudio.enhanced.actions.selectAll')}
                    </Button>
                  </Stack>
                </Box>

                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="caption">
                    <strong>{t('schoolAdmin.reportCardStudio.studio.meta.template')}:</strong> {templatePreview.label}<br />
                    <strong>{t('schoolAdmin.reportCardStudio.studio.meta.format')}:</strong> {t('schoolAdmin.reportCardStudio.studio.meta.formatValue')}<br />
                    <strong>{t('schoolAdmin.reportCardStudio.studio.meta.type')}:</strong> {
                      reportType === 'sequence'
                        ? t('schoolAdmin.reportCardStudio.studio.meta.typeSequence', { number: sequenceNumber })
                        : reportType === 'annual'
                          ? t('schoolAdmin.reportCardStudio.studio.meta.typeAnnual')
                          : t('schoolAdmin.reportCardStudio.studio.meta.typeTerm', { number: termNumber })
                    }<br />
                    <strong>{t('schoolAdmin.reportCardStudio.studio.meta.year')}:</strong> {academicYear}
                  </Typography>
                </Alert>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Panel - Student List */}
        <Grid item xs={12} md={8}>
          <Card sx={{ borderRadius: 3 }}>
            <CardHeader 
              title={t('schoolAdmin.reportCardStudio.studio.table.title')}
              subheader={t('schoolAdmin.reportCardStudio.studio.messages.studentsInSelectedClass', { count: students.length })}
            />
            <CardContent>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : students.length === 0 ? (
                <Alert severity="info">
                  {selectedClass ? t('schoolAdmin.reportCardStudio.studio.messages.noStudentsInClass') : t('schoolAdmin.reportCardStudio.studio.messages.selectClassToViewStudents')}
                </Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ width: '100%', overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 640 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedStudents.length === students.length && students.length > 0}
                            indeterminate={selectedStudents.length > 0 && selectedStudents.length < students.length}
                            onChange={toggleSelectAll}
                          />
                        </TableCell>
                        <TableCell><strong>{t('schoolAdmin.reportCardStudio.studio.table.headers.matricule')}</strong></TableCell>
                        <TableCell><strong>{t('schoolAdmin.reportCardStudio.studio.table.headers.name')}</strong></TableCell>
                        <TableCell><strong>{t('schoolAdmin.reportCardStudio.studio.table.headers.gender')}</strong></TableCell>
                        <TableCell align="center"><strong>{t('schoolAdmin.reportCardStudio.studio.table.headers.actions')}</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {students.map((student) => (
                        <TableRow 
                          key={student.id}
                          hover
                          selected={selectedStudents.includes(student.id)}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedStudents.includes(student.id)}
                              onChange={() => toggleStudentSelection(student.id)}
                            />
                          </TableCell>
                          <TableCell>{student.studentNumber || student.matricule || t('common.na')}</TableCell>
                          <TableCell>
                            {student.firstName} {student.lastName}
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={student.sex || student.gender || t('common.na')} 
                              size="small" 
                              color={(student.sex || student.gender) === 'M' ? 'primary' : 'secondary'}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title={t('schoolAdmin.reportCardStudio.studio.tooltips.generate')}>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => generateSingleReport(student.id)}
                                disabled={generating}
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          {/* Info Cards */}
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                <Typography variant="h4" color="primary" fontWeight={700}>
                  {students.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('schoolAdmin.reportCardStudio.studio.infoCards.totalStudents')}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                <Typography variant="h4" color="secondary" fontWeight={700}>
                  {selectedStudents.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('schoolAdmin.reportCardStudio.studio.infoCards.selected')}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                <Typography variant="h4" color="success.main" fontWeight={700}>
                  {
                    reportType === 'sequence'
                      ? t('schoolAdmin.reportCardStudio.studio.reportTypeAbbr.sequence', { number: sequenceNumber })
                      : reportType === 'annual'
                        ? t('schoolAdmin.reportCardStudio.studio.reportTypeAbbr.annual')
                        : t('schoolAdmin.reportCardStudio.studio.reportTypeAbbr.term', { number: termNumber })
                  }
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('schoolAdmin.reportCardStudio.studio.infoCards.reportType')}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Dialog
        open={pdfPreviewOpen}
        onClose={() => setPdfPreviewOpen(false)}
        fullScreen
        PaperProps={{
          sx: {
            backgroundColor: '#0f172a',
            color: '#f8fafc'
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              {dockTitle}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(248,250,252,0.7)' }}>
              {t('schoolAdmin.reportCardStudio.studio.preview.subtitle')}
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button size="small" variant="contained" color="secondary" onClick={downloadCurrentPdf} disabled={!hasTabs}>{t('schoolAdmin.reportCardStudio.studio.preview.actions.download')}</Button>
              <Button size="small" variant="contained" color="secondary" onClick={printCurrentPdf} disabled={!hasTabs}>{t('schoolAdmin.reportCardStudio.studio.preview.actions.print')}</Button>
              <Button size="small" variant="contained" color="secondary" onClick={openPdfInWindow} disabled={!hasTabs}>{t('schoolAdmin.reportCardStudio.studio.preview.actions.open')}</Button>
            </Stack>
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              onClick={() => hasTabs && closePdfTab(activePdfTab)}
              disabled={!hasTabs}
            >
              {t('schoolAdmin.reportCardStudio.studio.preview.actions.closeTab')}
            </Button>
            <Button
              variant="text"
              color="inherit"
              size="small"
              onClick={clearPdfTabs}
              disabled={!hasTabs}
            >
              {t('schoolAdmin.reportCardStudio.studio.preview.actions.clearAll')}
            </Button>
            <IconButton onClick={() => setPdfPreviewOpen(false)} color="inherit" sx={{ alignSelf: { xs: 'flex-end', sm: 'center' } }}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ minHeight: '80vh', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, overflow: 'hidden' }}>
          {!hasTabs ? (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
              <Typography variant="body1">{t('schoolAdmin.reportCardStudio.studio.messages.noReportsYet')}</Typography>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  width: { xs: '100%', md: 320 },
                  flexShrink: 0,
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: '1px solid rgba(148,163,184,0.25)',
                  backgroundColor: 'rgba(15,23,42,0.6)',
                  display: 'flex',
                  flexDirection: 'column',
                  maxHeight: { xs: 220, md: 'none' }
                }}
              >
                <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid rgba(148,163,184,0.18)' }}>
                  <Typography variant="subtitle2" sx={{ color: '#e2e8f0' }}>
                    {t('schoolAdmin.reportCardStudio.studio.preview.sidebar.title')}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(248,250,252,0.7)' }}>
                    {t('schoolAdmin.reportCardStudio.studio.preview.sidebar.count', { count: dockTabs.length })}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, overflowY: 'auto' }}>
                  <List dense disablePadding>
                    {dockTabs.map(({ tab, index }) => (
                      <React.Fragment key={tab.id}>
                        <ListItemButton
                          selected={index === activePdfTab}
                          onClick={() => setActivePdfTab(index)}
                          sx={{
                            px: 2,
                            '&.Mui-selected': { backgroundColor: 'rgba(249,115,22,0.18)' },
                            '&.Mui-selected:hover': { backgroundColor: 'rgba(249,115,22,0.24)' }
                          }}
                        >
                          <ListItemText
                            primary={tab.label}
                            secondary={tab?.studentMeta?.studentNumber || tab?.studentMeta?.matricule || ''}
                            primaryTypographyProps={{
                              variant: 'body2',
                              sx: { color: '#e2e8f0', fontWeight: 600 }
                            }}
                            secondaryTypographyProps={{
                              variant: 'caption',
                              sx: { color: 'rgba(248,250,252,0.65)' }
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              closePdfTab(index);
                            }}
                            sx={{ color: 'rgba(248,250,252,0.7)' }}
                          >
                            <CloseIcon fontSize="inherit" />
                          </IconButton>
                        </ListItemButton>
                        <Divider sx={{ borderColor: 'rgba(148,163,184,0.12)' }} />
                      </React.Fragment>
                    ))}
                  </List>
                </Box>
              </Box>

              <Box sx={{ flex: 1, borderRadius: 2, overflow: 'hidden', boxShadow: '0 20px 45px rgba(15,23,42,0.6)', minHeight: { xs: '60vh', md: 'auto' } }}>
                {currentTab ? (
                  <Box sx={{ width: '100%', height: '100%', overflowX: 'auto', bgcolor: '#1e293b' }}>
                    <iframe
                      id={`report-preview-${currentTab.id}`}
                      title={currentTab.label}
                      src={currentTab.url}
                      style={{ width: '100%', height: '100%', minHeight: '60vh', border: 'none', backgroundColor: '#1e293b' }}
                    />
                  </Box>
                ) : null}
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReportCardStudioPage;
